import { pickLocalized, DEFAULT_LOCALE } from "@/lib/i18n";
import { urlFor } from "@/lib/sanity";

export const BASE_URL = "https://www.matcha-mind.com";
export const SITE_NAME = "Matchamind";

export function absoluteUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

/** Flattens Portable Text blocks to a single plain-text string (empty when there is no text). */
export function plainTextFromBlocks(blocks: unknown): string {
  if (!Array.isArray(blocks)) return "";
  return blocks
    .filter((b: any) => b?._type === "block" && Array.isArray(b.children))
    .map((b: any) => b.children.map((c: any) => c?.text ?? "").join(""))
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Word count of Portable Text body copy, for Article JSON-LD. Undefined when empty. */
export function wordCountFromBlocks(blocks: unknown): number | undefined {
  const text = plainTextFromBlocks(blocks);
  if (!text) return undefined;
  return text.split(" ").filter(Boolean).length;
}

/**
 * Plain-text summary drawn from the first blocks of Portable Text, for meta
 * descriptions when a page has no authored excerpt. Returns undefined when the
 * content has no text so callers can fall back to a static default.
 */
export function excerptFromBlocks(blocks: unknown, maxLen = 155): string | undefined {
  const text = plainTextFromBlocks(blocks);
  if (!text) return undefined;
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1).replace(/\s+\S*$/, "") + "…";
}

interface PageMetaOptions {
  title: string;
  /** Omitted from the page entirely when unset — search engines then pick their own snippet. */
  description?: string;
  /** Route path starting with "/" — canonical strips search params (?lang) by design. */
  path: string;
  ogImage?: string;
  /** Alt text for the OG image, so social/AI cards describe the picture. */
  ogImageAlt?: string;
  ogType?: "website" | "article";
  /** Article-only OpenGraph timestamps (ISO 8601). */
  publishedTime?: string;
  modifiedTime?: string;
}

/**
 * Standard per-route head() payload: title, description, OpenGraph and an
 * absolute canonical URL. Meta is always in the default locale (en) — the
 * canonical URL is the ?lang-free English page.
 */
export function pageMeta({
  title,
  description,
  path,
  ogImage,
  ogImageAlt,
  ogType = "website",
  publishedTime,
  modifiedTime,
}: PageMetaOptions) {
  const url = absoluteUrl(path);
  return {
    meta: [
      { title },
      ...(description
        ? [
            { name: "description", content: description },
            { property: "og:description", content: description },
            { name: "twitter:description", content: description },
          ]
        : []),
      { property: "og:title", content: title },
      { name: "twitter:title", content: title },
      { property: "og:type", content: ogType },
      { property: "og:url", content: url },
      ...(ogImage
        ? [
            { property: "og:image", content: ogImage },
            { name: "twitter:image", content: ogImage },
            ...(ogImageAlt
              ? [
                  { property: "og:image:alt", content: ogImageAlt },
                  { name: "twitter:image:alt", content: ogImageAlt },
                ]
              : []),
          ]
        : []),
      ...(ogType === "article" && publishedTime
        ? [{ property: "article:published_time", content: publishedTime }]
        : []),
      ...(ogType === "article" && modifiedTime
        ? [{ property: "article:modified_time", content: modifiedTime }]
        : []),
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

/** 1200×630 crop of a Sanity image for og:image, or undefined. */
export function ogImageUrl(image: unknown): string | undefined {
  if (!image) return undefined;
  return urlFor(image as Parameters<typeof urlFor>[0])
    .width(1200)
    .height(630)
    .fit("crop")
    .url();
}

/** Renders a JSON-LD script tag. Server-rendered in page JSX (valid anywhere in the document). */
export function JsonLd({ data }: { data: object }) {
  return (
    <script
      type="application/ld+json"
      // "<" escaped to prevent </script> breakout from CMS strings
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data).replace(/</g, "\\u003c") }}
    />
  );
}

export const organizationLd = {
  "@type": "Organization",
  name: SITE_NAME,
  url: BASE_URL,
  logo: absoluteUrl("/apple-touch-icon.png"),
  // Brand socials + studio: machine-readable version of the footer credit,
  // ties the Matchamind/Anatol Labs entities together for search & AI answers.
  sameAs: ["https://www.instagram.com/matchamind.app/", "https://www.facebook.com/matchamind"],
  parentOrganization: {
    "@type": "Organization",
    name: "Anatol Labs",
    sameAs: ["https://www.linkedin.com/company/anatol-labs/"],
  },
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: BASE_URL,
  publisher: organizationLd,
};

interface ArticleLdSource {
  title?: string;
  author?: string;
  journal?: string;
  year?: number | string;
  url?: string;
}

interface ArticleLdOptions {
  title: unknown;
  description?: string;
  path: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  /** Topic bucket shown on the page (the post's category). */
  section?: string;
  /** Body word count — a length signal for engines ranking depth. */
  wordCount?: number;
  /** Authored reading time in minutes, emitted as an ISO 8601 duration. */
  readingTimeMinutes?: number;
  /** The article's reference list, so the sources it leans on are machine-readable. */
  sources?: ArticleLdSource[];
  /** Collection the article belongs to — sets the article subtype and links back to its listing. */
  collection?: "blog" | "news";
}

/** One reference from a post's source list, as a citable scholarly work. */
function citationLd(source: ArticleLdSource) {
  const name = source.title?.trim();
  if (!name) return undefined;
  return {
    "@type": "ScholarlyArticle",
    name,
    ...(source.author?.trim() ? { author: { "@type": "Person", name: source.author.trim() } } : {}),
    ...(source.journal?.trim()
      ? { isPartOf: { "@type": "Periodical", name: source.journal.trim() } }
      : {}),
    ...(source.year ? { datePublished: String(source.year) } : {}),
    ...(source.url ? { url: source.url } : {}),
  };
}

export function articleLd({
  title,
  description,
  path,
  image,
  datePublished,
  dateModified,
  section,
  wordCount,
  readingTimeMinutes,
  sources,
  collection,
}: ArticleLdOptions) {
  const citations = (sources ?? []).map(citationLd).filter(Boolean);
  return {
    "@context": "https://schema.org",
    "@type":
      collection === "blog" ? "BlogPosting" : collection === "news" ? "NewsArticle" : "Article",
    headline: pickLocalized(title as any, DEFAULT_LOCALE),
    ...(description ? { description } : {}),
    mainEntityOfPage: absoluteUrl(path),
    ...(image ? { image } : {}),
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified ? { dateModified } : {}),
    // JSON-LD always describes the default-locale (en) rendering of the page.
    inLanguage: DEFAULT_LOCALE,
    ...(section ? { articleSection: section } : {}),
    ...(wordCount ? { wordCount } : {}),
    ...(readingTimeMinutes ? { timeRequired: `PT${readingTimeMinutes}M` } : {}),
    ...(citations.length ? { citation: citations } : {}),
    ...(collection === "blog"
      ? {
          isPartOf: {
            "@type": "Blog",
            name: `${SITE_NAME} Blog`,
            url: absoluteUrl("/blog"),
          },
        }
      : {}),
    ...(collection === "news"
      ? {
          isPartOf: {
            "@type": "CollectionPage",
            name: `${SITE_NAME} News`,
            url: absoluteUrl("/news"),
          },
        }
      : {}),
    author: organizationLd,
    publisher: organizationLd,
  };
}

/** Trail from the site root to the current page, mirroring the URL hierarchy. */
export function breadcrumbLd(trail: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

interface ListingEntry {
  title: unknown;
  path: string;
  description?: string;
  image?: string;
  datePublished?: string;
}

/** The blog index as a Blog with its posts listed in order — the listing itself becomes citable. */
export function blogListingLd({
  name,
  description,
  posts,
}: {
  name: string;
  description?: string;
  posts: ListingEntry[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name,
    ...(description ? { description } : {}),
    url: absoluteUrl("/blog"),
    inLanguage: DEFAULT_LOCALE,
    publisher: organizationLd,
    blogPost: posts.map((post) => ({
      "@type": "BlogPosting",
      headline: pickLocalized(post.title as any, DEFAULT_LOCALE),
      url: absoluteUrl(post.path),
      mainEntityOfPage: absoluteUrl(post.path),
      ...(post.description ? { description: post.description } : {}),
      ...(post.image ? { image: post.image } : {}),
      ...(post.datePublished ? { datePublished: post.datePublished } : {}),
      author: organizationLd,
      publisher: organizationLd,
    })),
  };
}

/** The news index as a CollectionPage listing its announcements newest-first. */
export function newsListingLd({
  name,
  description,
  posts,
}: {
  name: string;
  description?: string;
  posts: ListingEntry[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    ...(description ? { description } : {}),
    url: absoluteUrl("/news"),
    inLanguage: DEFAULT_LOCALE,
    publisher: organizationLd,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: posts.map((post, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "NewsArticle",
          headline: pickLocalized(post.title as any, DEFAULT_LOCALE),
          url: absoluteUrl(post.path),
          mainEntityOfPage: absoluteUrl(post.path),
          ...(post.description ? { description: post.description } : {}),
          ...(post.image ? { image: post.image } : {}),
          ...(post.datePublished ? { datePublished: post.datePublished } : {}),
          author: organizationLd,
          publisher: organizationLd,
        },
      })),
    },
  };
}

export function faqLd(items: Array<{ question: unknown; answer: unknown }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: pickLocalized(item.question as any, DEFAULT_LOCALE),
      acceptedAnswer: {
        "@type": "Answer",
        text: pickLocalized(item.answer as any, DEFAULT_LOCALE),
      },
    })),
  };
}
